import type { InvestorData } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import { EmptyState } from '@workspace/ui/components/empty-state'
import { LabelValue } from '@workspace/ui/components/label-value'
import { ProfileHeader } from '@workspace/ui/components/profile-header'
import { ProfileSection } from '@workspace/ui/components/profile-section'
import { Eye, FileText, Globe, MapPin, Pencil } from 'lucide-react'
import { useState } from 'react'
import { DocumentPreviewModal, type PreviewDocument } from '@/features/preview'
import { useInvestorConfigQuery } from '../../../hooks/api'

type InvestorViewProps = {
  investor: InvestorData
  canEdit: boolean
  onEdit?: () => void
}

export function InvestorView({ investor, canEdit, onEdit }: InvestorViewProps) {
  const { data: investorConfigResponse } = useInvestorConfigQuery()

  const thesisDocument = investor?.thesisDocument
  const investmentAreasOptions = investorConfigResponse?.data?.investmentAreas ?? []
  const investmentRegionsOptions = investorConfigResponse?.data?.investmentRegions ?? []

  const [previewDocument, setPreviewDocument] = useState<PreviewDocument | null>(null)

  // Helper to get label from value
  const getAreaLabel = (value: string) =>
    investmentAreasOptions.find((o) => o.value === value)?.label ?? value
  const getRegionLabel = (value: string) =>
    investmentRegionsOptions.find((o) => o.value === value)?.label ?? value

  return (
    <>
      <ProfileHeader
        coverImageUrl={investor.coverImageUrl}
        avatarUrl={investor.logoUrl}
        name={investor.name}
        actions={
          canEdit &&
          onEdit && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Pencil className="size-4" />
              Edit Profile
            </Button>
          )
        }
        contentClassName="mx-auto max-w-6xl"
      />

      <div className="mx-auto w-full max-w-6xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex flex-wrap items-center gap-6">
            {investor.websiteUrl && (
              <a
                href={investor.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                <LabelValue
                  icon={<Globe className="size-4" />}
                  label="Website"
                  value={new URL(investor.websiteUrl).hostname}
                  direction="horizontal"
                />
              </a>
            )}
          </div>
          {investor.investmentGeographies.length > 0 && (
            <div className="flex flex-wrap gap-2 sm:ml-auto">
              {investor.investmentGeographies.map((geo) => (
                <Badge key={geo} variant="secondary" size="md" className="gap-1">
                  <MapPin className="size-3" />
                  {getRegionLabel(geo)}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {investor.investmentAreas.length > 0 && (
          <ProfileSection title="Investment Focus">
            <div className="flex flex-wrap gap-2">
              {investor.investmentAreas.map((area) => (
                <Badge key={area} variant="secondary" size="md">
                  {getAreaLabel(area)}
                </Badge>
              ))}
            </div>
          </ProfileSection>
        )}

        <ProfileSection title="Investment Thesis">
          {thesisDocument ? (
            <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-card">
              <div className="flex items-center gap-3">
                <FileText className="size-8 text-muted-foreground" />
                <div>
                  <p className="font-medium text-sm">{thesisDocument.fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {thesisDocument.fileSize
                      ? `${Math.round(thesisDocument.fileSize / 1024)} KB`
                      : 'Document'}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setPreviewDocument({
                    id: thesisDocument.id,
                    fileName: thesisDocument.fileName,
                    mimeType: thesisDocument.mimeType,
                  })
                }
              >
                <Eye className="size-4" />
                Preview
              </Button>
            </div>
          ) : (
            <EmptyState
              icon={<FileText className="size-8" />}
              title="No investment thesis uploaded"
              description="Upload your investment thesis to help founders understand your focus"
            />
          )}
        </ProfileSection>
      </div>

      {previewDocument && (
        <DocumentPreviewModal
          document={previewDocument}
          open
          onOpenChange={(open) => !open && setPreviewDocument(null)}
        />
      )}
    </>
  )
}
